/* import __COLOCATED_TEMPLATE__ from './sq-option-checkbox.hbs'; */
/**
A Checkbox with a label

A basic checkbox component with a label

```example
@title Basic Usage
@description Pass a label and optional secondary label.

@hbs
{{sq-option-checkbox name="option-checkbox"
                     label="Unchecked"}}

{{sq-option-checkbox name="option-checkbox-checked"
                     checked=true
                     label="Checked"}}

{{sq-option-checkbox name="option-checkbox-indeterminate"
                     checked="indeterminate"
                     label="Indeterminate"}}

{{sq-option-checkbox name="option-checkbox-disabled"
                     disabled=true
                     label="Unchecked (Disabled)"}}

{{sq-option-checkbox name="option-checkbox-disabled-checked"
                     checked=true disabled=true
                     label="Checked (Disabled)"}}

{{sq-option-checkbox name="option-checkbox-disabled-indeterminate"
                     checked="indeterminate"
                     disabled=true
                     label="Indeterminate (Disabled)"}}

```

```example
@title With Secondary Text
@description You can pass a secondary label to the checkbox

@hbs
{{sq-option-checkbox name="option-checkbox-with-secondary-label"
                     label="Unchecked"
                     labelSecondary="Default"}}

{{sq-option-checkbox name="option-checkbox-with-secondary-label-checked"
                     checked=true
                     label="Checked"
                     labelSecondary="Default"}}

{{sq-option-checkbox name="option-checkbox-with-secondary-label-indeterminate"
                     checked="indeterminate"
                     label="Indeterminate"
                     labelSecondary="Default"}}

{{sq-option-checkbox name="option-checkbox-with-secondary-label-disabled"
                     disabled=true
                     label="Unchecked"
                     labelSecondary="Disabled"}}

{{sq-option-checkbox name="option-checkbox-with-secondary-label-disabled-checked"
                     checked=true disabled=true
                     label="Checked"
                     labelSecondary="Disabled"}}

{{sq-option-checkbox name="option-checkbox-with-secondary-label-disabled-indeterminate"
                     checked="indeterminate"
                     disabled=true
                     label="Indeterminate"
                     labelSecondary="Disabled"}}
```

```example
@title Contextual Labels
@description If you need to do some sort of fancy formatting for your labels, you can output each one
separately using contextual components

@hbs
{{#sq-option-checkbox name="option-checkbox-block" as |checkbox|}}
  {{#checkbox.label}}
    {{svgs/alert-icon}} Oh No, something is wrong
  {{/checkbox.label}}
  {{#checkbox.label-secondary}}
    Please correct this <em>egregious</em> error.
  {{/checkbox.label-secondary}}
{{/sq-option-checkbox}}

```

```example
@title Block Content
@description You can also yield completely custom content in addition to the primary
and secondary labels by passing a block directly to the checkbox option

@hbs
{{#sq-option-checkbox name="option-checkbox-block"
                      label="Select this custom option"}}
  <input class="input" type="text" placeholder="some kind of extra field">
{{/sq-option-checkbox}}

```

@param {String} label - The primary label for this field
@param {String} labelSecondary - Optional. Secondary label for this field.
@param {String} name - A name for this field.
@param {Any} value - A value that this checkbox represents. (NOT the checkbox's state. Use the `checked` param for boolean or indeterminate state. The value should be a meaningful string.)
@param {Boolean/String} [checked=false] - true, false or 'indeterminate'
@param {Boolean} [disabled=false] - Disable the checkbox
@param {Number} [tabindex=0] - passes through to the input giving it a tabindex for keyboard navigation
@param {Function} action - An action to perform when the value of 'checked' changes. Is passed the new 'checked' value of the checkbox
*/

import Component from '@ember/component';
import { computed, set } from '@ember/object';
import { alias } from '@ember/object/computed';
import CHECKBOX_STATE from '@square/glass-ui/components/sq-checkbox';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  tagName: 'label',
  classNames: ['option', 'option--checkbox'],
  classNameBindings: ['checked:option--is-selected', 'checked:checkbox-option--is-checked', 'disabled:option--is-disabled'],
  attributeBindings: ['data-test-option-checkbox', 'data-test-option-checkbox-checked'],
  'data-test-option-checkbox': computed('_dataTestOptionCheckbox', 'label', 'value', {
    get() {
      if (this._dataTestOptionCheckbox !== undefined) {
        return this._dataTestOptionCheckbox;
      }
      return this.get('label') || this.get('value') || true;
    },
    set(_, value) {
      return set(this, '_dataTestOptionCheckbox', value);
    }
  }),
  'data-test-option-checkbox-checked': alias('checked'),
  state: CHECKBOX_STATE.UNCHECKED,
  testSelectorValue: computed('value', 'label', function () {
    return typeof this.value === 'object' ? this.label : this.value ?? true;
  })
});